import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from "react";

/** An activity just logged in one tap, held so the toast can offer Undo. */
export interface JustLogged {
  interactionId: string;
  tripId: string;
  /** What the toast says after "Logged", e.g. "Entered Line · Hagrid's". */
  label: string;
}

interface LogSheetContextValue {
  /** Opens the global Log-activity sheet against the user's current trip. */
  openLogSheet: () => void;
  closeLogSheet: () => void;
  open: boolean;
  /** The last one-tap log, while its Undo toast is up. */
  justLogged: JustLogged | null;
  announceLogged: (logged: JustLogged) => void;
  dismissLogged: () => void;
}

const LogSheetContext = createContext<LogSheetContextValue | null>(null);

/**
 * Lightweight global trigger for the Log-activity sheet so the bottom-nav
 * center button can open it from any tab. The actual sheet (which needs the
 * current trip's segments + interactions) is rendered by <GlobalLogSheet />
 * inside the authenticated Layout, keyed off this `open` flag.
 */
export function LogSheetProvider({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(false);
  const [justLogged, setJustLogged] = useState<JustLogged | null>(null);
  const openLogSheet = useCallback(() => setOpen(true), []);
  const closeLogSheet = useCallback(() => setOpen(false), []);
  const announceLogged = useCallback((logged: JustLogged) => setJustLogged(logged), []);
  const dismissLogged = useCallback(() => setJustLogged(null), []);
  const value = useMemo(
    () => ({ open, openLogSheet, closeLogSheet, justLogged, announceLogged, dismissLogged }),
    [open, openLogSheet, closeLogSheet, justLogged, announceLogged, dismissLogged],
  );
  return <LogSheetContext.Provider value={value}>{children}</LogSheetContext.Provider>;
}

export function useLogSheet(): LogSheetContextValue {
  const ctx = useContext(LogSheetContext);
  if (!ctx) {
    throw new Error("useLogSheet must be used within a LogSheetProvider");
  }
  return ctx;
}

/** The "Logged · Undo" toast's trigger, or null outside the app shell (tests). */
export function useAnnounceLogged(): ((logged: JustLogged) => void) | null {
  return useContext(LogSheetContext)?.announceLogged ?? null;
}

/**
 * Closes whichever Log sheet is showing. Every sheet host passes its own
 * close through `LogActivitySheet`, so the picker can shut it after a
 * one-tap log without each page wiring a callback. Null outside a sheet.
 */
export const SheetCloseContext = createContext<(() => void) | null>(null);

export function useCloseSheet(): (() => void) | null {
  return useContext(SheetCloseContext);
}
